import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { Utils } from 'src/app/core/resources/utils';
import { SnackBarService } from 'src/app/core/services/snackBar.service';
import { PreBill } from 'src/app/core/interfaces/preBill';
import { PreBillService } from 'src/app/core/services/pre-bill.service';
import { DateManager } from 'src/app/core/managers/date.manager';
import { DateEnum } from 'src/app/core/enums/date.enum';
import { Patterns } from 'src/app/core/resources/patterns';
import { Cargo } from 'src/app/core/interfaces/cargo';
import { CargoStateEnum } from 'src/app/core/enums/cargoState.enum';
import { FreightListService } from '../cargo-list/cargo-list.service';
import { Origin } from 'src/app/core/interfaces/origin';
import { Destination } from 'src/app/core/interfaces/destination';
import { SettingId } from 'src/app/core/interfaces/saas-config';
import { AuthService } from 'src/app/core/services/authentication.service';
import { ServiceMessages } from 'src/app/core/messages/service-messages.enum';
import { TransportCompany } from 'src/app/core/interfaces/transportCompany';
import { SaasTransportCompanyService } from 'src/app/core/services/saas-transport-company.service';

export type PreBillFormMode = 'create' | 'update';

@Component({
  selector: 'app-pre-bill-form',
  templateUrl: './pre-bill-form.component.html',
  styleUrls: ['./pre-bill-form.component.scss']
})
export class PreBillFormComponent implements OnInit {

  public mode: PreBillFormMode = 'create';
  public step: number = 1;

  public dateFromControl: FormControl = new FormControl(null, Validators.required);
  public dateToControl: FormControl = new FormControl(null, Validators.required);
  public companyControl: FormControl = new FormControl(null, Validators.required);
  public companyValidate: string = '';
  public consecutiveControl: FormControl = new FormControl(null);
  public cargos: { cargo: Cargo, errorMessage?: string, checked: boolean }[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: {
      mode: PreBillFormMode,
      preBill?: PreBill,
      // Empresa de transporte del listado (solo al crear); null = SaaS sin transporte.
      transportCompanyId?: string
    },
    public dialogRef: MatDialogRef<PreBillFormComponent>,
    public utils: Utils,
    private spinner: NgxSpinnerService,
    private snackBarService: SnackBarService,
    private preBillService: PreBillService,
    private cargoListService: FreightListService,
    private authService: AuthService,
    private patterns: Patterns,
    private saasTransportCompanyService: SaasTransportCompanyService
  ) {
    this.consecutiveControl = new FormControl(null, [
      Validators.pattern(this.patterns.ONLY_NUMBERS.source),
      Validators.minLength(1),
      Validators.maxLength(10)
    ]);
  }

  ngOnInit(): void {
    this.mode = this.data && this.data.mode ? this.data.mode : 'create';
    if (this.mode === 'update' && this.data && this.data.preBill) {
      this.step = 2;
      if (this.data.preBill.companyId)
        this.companyControl.setValue({ companyId: this.data.preBill.companyId });
      const cargos = this.data.preBill.cargos || [];
      this.cargos = cargos.map(cargo => ({ cargo, checked: true }));
    }
  }

  private get companyId(): string {
    return this.utils.getNestedValue(this.companyControl, 'value.companyId')
      || this.utils.getNestedValue(this.data, 'preBill.companyId')
      || null;
  }


  /** Al editar, la de la prefactura; al crear, la del listado. null = sin acotar (comportamiento previo). */
  public get transportCompanyId(): string {
    if (this.mode === 'update') return this.utils.getNestedValue(this.data, 'preBill.transportCompany.id') || null;
    return this.utils.getNestedValue(this.data, 'transportCompanyId') || null;
  }

  public get title(): string {
    if (this.mode === 'update') {
      const consecutive = this.data && this.data.preBill && this.data.preBill.consecutive
        ? ` #${this.data.preBill.consecutive}`
        : '';
      return `Editar prefactura${consecutive}`;
    }
    return this.step === 1 ? 'Generar prefactura' : 'Seleccionar servicios';
  }

  public get subtitle(): string {
    if (this.mode === 'update') {
      return 'Agrega o quita servicios y selecciona los que harán parte de la prefactura.';
    }
    if (this.step === 1) {
      return 'Selecciona un rango de fechas de origen y el cliente para filtrar los servicios que se incluirán en la prefactura.';
    }
    return 'Agrega servicios por ID y selecciona los que harán parte de la prefactura.';
  }

  async searchConsecutives() {
    if (!this.consecutiveControl.value)
      return this.snackBarService.openSnackBar("Ingrese los consecutivos de los servicios a replicar", undefined, 'alert');
    const consecutives: string[] = `${this.consecutiveControl.value}`.split(',').map(c => c.trim()).filter(c => !!c);
    if (!consecutives.length)
      return this.snackBarService.openSnackBar("Ingrese al menos un consecutivo", undefined, 'alert');
    const consecutivesToFind: string[] = consecutives.filter(
      consecutive => !this.cargos.some(selected => selected && selected.cargo && selected.cargo.consecutive.toString() === consecutive)
    );
    if (!consecutivesToFind.length)
      return this.snackBarService.openSnackBar("Todos los servicios ya están en la lista", undefined, 'alert');
    const listCargo = await this.findCargos('consecutives', consecutivesToFind);
    if (!listCargo || !listCargo.length)
      return this.snackBarService.openSnackBar("No se encontraron servicios con los consecutivos digitados", undefined, 'alert');
    this.cargos.push(...listCargo.map(cargo => this.processCargo(cargo)));
    const nonExistentList: string[] = consecutivesToFind.filter(
      consecutive => !listCargo.some(cargo => cargo && cargo.consecutive.toString() === consecutive)
    );
    if (nonExistentList.length > 0)
      this.snackBarService.openSnackBar(`No se encontraron servicios con los consecutivos: ${nonExistentList.join(', ')}`, undefined, 'alert');
    else
      this.snackBarService.openSnackBar("Servicios encontrados y agregados correctamente", undefined, 'success');
    this.consecutiveControl.setValue('');
  }

  async refreshCargos() {
    if (!this.cargos.length) return;
    const consecutives: string[] = this.cargos
      .map(item => item && item.cargo && item.cargo.consecutive != null ? item.cargo.consecutive.toString() : null)
      .filter(consecutive => !!consecutive);
    if (!consecutives.length) return;
    const listCargo = await this.findCargos('consecutives', consecutives);
    if (!listCargo || !listCargo.length)
      return this.snackBarService.openSnackBar("No se encontraron servicios con los consecutivos del listado", undefined, 'alert');
    this.cargos = listCargo.map(cargo => this.processCargo(cargo));
    const nonExistentList: string[] = consecutives.filter(
      consecutive => !listCargo.some(cargo => cargo && cargo.consecutive.toString() === consecutive)
    );
    if (nonExistentList.length > 0)
      this.snackBarService.openSnackBar(`No se encontraron servicios con los consecutivos: ${nonExistentList.join(', ')}`, undefined, 'alert');
    else
      this.snackBarService.openSnackBar("Servicios actualizados correctamente", undefined, 'success');
  }

  async searchInitialCargos() {
    this.dateFromControl.markAsTouched();
    this.dateToControl.markAsTouched();
    this.companyControl.markAsTouched();
    this.companyValidate = 'touched';
    if (this.utils.errorMessagesCustomized(this.companyControl, 'cliente')) return;
    if (this.utils.errorMessagesCustomized(this.dateFromControl, 'Desde')) return;
    if (this.utils.errorMessagesCustomized(this.dateToControl, 'Hasta')) return;
    const cargos = await this.findCargos('initial');
    this.cargos.push(...cargos.map(cargo => this.processCargo(cargo)));
    if (cargos.length) this.step = 2;
    else this.snackBarService.openSnackBar('No se encontraron servicios con los filtros seleccionados', undefined, 'alert');
  }

  processCargo(cargo: Cargo): { cargo: Cargo, errorMessage?: string, checked: boolean } {
    if (!cargo) return { cargo, errorMessage: 'Servicio inválido', checked: false };
    if (![CargoStateEnum.END_SERVICE, CargoStateEnum.START_SERVICE].includes(cargo.state))
      return { cargo, errorMessage: `El servicio ${cargo.consecutive} no está en un estado apto para ser seleccionado`, checked: false };
    if (cargo.idCompany && cargo.idCompany !== this.companyId)
      return { cargo, errorMessage: `El servicio ${cargo.consecutive} no pertenece al cliente seleccionado`, checked: false };
    if (this.transportCompanyId) {
      const cargoTransportCompanyId = this.utils.getNestedValue(cargo, 'transportCompany.id');
      if (!cargoTransportCompanyId)
        return { cargo, errorMessage: ServiceMessages.CARGO_WITHOUT_TRANSPORT_COMPANY, checked: false };
      if (cargoTransportCompanyId !== this.transportCompanyId)
        return { cargo, errorMessage: ServiceMessages.CARGO_NOT_FROM_PRE_BILL_TRANSPORT_COMPANY, checked: false };
    }
    const dataPreBillConsecutive = this.utils.getNestedValue(this.data, 'preBill.consecutive');
    if (cargo.preBill && (!dataPreBillConsecutive || dataPreBillConsecutive != cargo.preBill))
      return { cargo, errorMessage: `El servicio ${cargo.consecutive} ya tiene una prefactura asociada (Prefactura #${cargo.preBill})`, checked: false };
    if (cargo.shippingCost && cargo.shippingCost.cashed)
      return { cargo, errorMessage: `El servicio ${cargo.consecutive} ya ha sido facturado`, checked: false };
    if (!cargo.requiresPreBill)
      return { cargo, errorMessage: `El servicio ${cargo.consecutive} no requiere prefactura`, checked: false };
    const requiresPreValidation = this.authService && this.authService.hasCompanySettingEnabled(SettingId.REQUIRES_PRE_VALIDATION);
    if (requiresPreValidation && (!cargo.prevalidation || !cargo.prevalidation.valid))
      return { cargo, errorMessage: `El servicio ${cargo.consecutive} no ha sido validado para facturación`, checked: false };

    return { cargo, checked: true };
  }

  private async findCargos(mode: 'consecutives' | 'initial', values?: string[]): Promise<Cargo[]> {
    try {
      this.spinner.show();
      let filters = 'cashed=false';
      if (mode === 'consecutives') filters += `&consecutives=${encodeURIComponent(JSON.stringify(values))}`;
      else if (mode === 'initial') {
        filters += `&states=${encodeURIComponent(JSON.stringify([CargoStateEnum.END_SERVICE, CargoStateEnum.START_SERVICE]))}&pageKey=1&pageSize=200`;
        filters += `&nit=${encodeURIComponent(this.companyId)}`;
        if (this.transportCompanyId) filters += `&transportCompanyId=${encodeURIComponent(this.transportCompanyId)}`;
        filters += `&dateLoad=${encodeURIComponent(DateManager.formatDate(this.dateFromControl.value, null, 'YYYY-MM-DD ZZ'))}`;
        filters += `&endDate=${encodeURIComponent(DateManager.formatDate(this.dateToControl.value, null, 'YYYY-MM-DD ZZ'))}`;
      }
      const results = await this.cargoListService.getListCargo(filters).toPromise();
      this.spinner.hide();
      if (!results || !results.results || !results.results.length) return [];
      return results.results;
    } catch (error) {
      this.spinner.hide();
      return [];
    }
  }

  public getCargoRate(cargo: Cargo): number {
    const rate = this.utils.getNestedValue(cargo, 'shippingCost.rate');
    return rate ? rate : null;
  }

  public getCargoOrigin(cargo: Cargo): string {
    const origin: Origin = this.utils.getNestedValue(cargo, 'cargoFeature.uploadDownload.origin');
    return origin ? origin.name : null;
  }

  public getCargoDestination(cargo: Cargo): string {
    const destination: Destination[] = this.utils.getNestedValue(cargo, 'cargoFeature.uploadDownload.destination');
    return destination && destination.length ? destination[destination.length - 1].name : null;
  }

  public getConsignmentsLabel(cargo: Cargo): string {
    if (!cargo) return null;
    const consignments = this.utils.getCargoAssociatedConsignments(cargo);
    if (!consignments || !consignments.length) return null;
    return consignments.join(', ');
  }

  get someCargoHasManifest(): boolean {
    return this.cargos.some(item => !!this.utils.getNestedValue(item, 'cargo.manifest'));
  }

  get someCargoHasConsignments(): boolean {
    return this.cargos.some(item => !!this.getConsignmentsLabel(item.cargo));
  }

  public get selectedCargos(): Cargo[] {
    return this.cargos.filter(item => item.checked).map(item => item.cargo);
  }

  public get selectedCount(): number {
    return this.selectedCargos.length;
  }

  public backStep(): void {
    if (this.mode === 'create' && this.step === 2) {
      this.step = 1;
      this.cargos = [];
    }
  }

  public trackByCargo(index: number, item: { cargo: Cargo, errorMessage?: string, checked: boolean }): string | number {
    return item && item.cargo && item.cargo.id ? item.cargo.id : index;
  }

  /** Objeto que se guarda en la prefactura, igual al de los servicios: { id: NIT, name }. */
  private async getTransportCompanyToSave(): Promise<TransportCompany> {
    const id = this.transportCompanyId;
    if (!id) return null;
    let name: string = null;
    try {
      const company = await this.saasTransportCompanyService.getById(id).toPromise();
      name = company && company.name ? company.name : null;
    } catch (error) { }
    return name ? { id, name } : { id };
  }

  public async onSubmit(): Promise<void> {
    const selected = this.selectedCargos;
    if (!selected.length)
      return this.snackBarService.openSnackBar('Selecciona al menos un servicio', undefined, 'alert');
    if (this.mode === 'create' && !this.companyId)
      return this.snackBarService.openSnackBar('Debe seleccionar un cliente', undefined, 'alert');
    this.spinner.show();
    const request$ = this.mode === 'update' && this.data && this.data.preBill
      ? this.preBillService.updatePreBill(this.data.preBill, selected)
      : this.preBillService.createPreBill(this.companyId, selected, await this.getTransportCompanyToSave());

    request$.subscribe(
      (preBill: PreBill) => {
        this.spinner.hide();
        const message = this.mode === 'update' ? 'Prefactura actualizada exitosamente' : 'Prefactura generada exitosamente';
        this.snackBarService.openSnackBar(message, undefined, 'success');
        this.dialogRef.close({ state: true, preBill });
      },
      (error) => {
        this.spinner.hide();
        const message = error && error.error && error.error.message ? error.error.message :
          this.mode === 'update'
            ? 'Ocurrió un error al actualizar la prefactura'
            : 'Ocurrió un error al generar la prefactura';
        this.snackBarService.openSnackBar(message, undefined, 'error');
      }
    );
  }

  public openCargoDetail(cargo: Cargo): void {
    this.utils.openCargoNewTab(cargo);
  }
}
